import { useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api, formatError } from "../lib/api";
import ShareDialog from "../components/ShareDialog";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  AlertTriangle,
  Clock,
  Users,
  Phone,
  Ban,
} from "lucide-react";

const VERDICT_META = {
  danger: {
    color: "#B91C1C",
    bg: "#FEF2F2",
    border: "#DC2626",
    icon: ShieldAlert,
    labelKey: "shield_verdict_danger",
    tone: "bg-[#B91C1C] text-white",
  },
  caution: {
    color: "#E59D32",
    bg: "#FEF3C7",
    border: "#F59E0B",
    icon: AlertTriangle,
    labelKey: "shield_verdict_caution",
    tone: "bg-[#E59D32] text-white",
  },
  trusted: {
    color: "#A762B1",
    bg: "#F0FDF4",
    border: "#A762B1",
    icon: ShieldCheck,
    labelKey: "shield_verdict_trusted",
    tone: "bg-[#A762B1] text-white",
  },
  unknown: {
    color: "#101A42",
    bg: "#F8EFD9",
    border: "#101A42",
    icon: ShieldQuestion,
    labelKey: "shield_verdict_unknown",
    tone: "bg-[#101A42] text-white",
  },
};

export default function Shield() {
  const { t, lang } = useLang();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/scam/check", { text });
      setResult(data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const meta = result ? VERDICT_META[result.verdict] || VERDICT_META.unknown : null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="shield-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#B91C1C]/10 text-[#B91C1C]">
          <ShieldAlert className="h-7 w-7" strokeWidth={2} />
        </div>
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest text-[#B91C1C]">
            {t("shield")}
          </p>
          <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
            {t("shield_title")}
          </h1>
        </div>
      </div>

      <p className="text-lg leading-relaxed text-[#333]">{t("shield_intro")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <Textarea
          data-testid="shield-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder={t("shield_placeholder")}
          className="rounded-2xl border-2 text-lg"
        />
        <Button
          type="submit"
          disabled={loading || !text.trim()}
          data-testid="shield-check-btn"
          className="min-h-[60px] w-full rounded-full bg-[#B91C1C] text-lg font-semibold text-white hover:bg-[#DC2626] sm:w-auto sm:px-10"
        >
          {loading ? t("shield_analyzing") : t("shield_check_btn")}
        </Button>
      </form>

      {error && (
        <div
          data-testid="shield-error"
          className="mt-6 rounded-xl border-2 border-[#B91C1C]/30 bg-[#B91C1C]/10 p-4 text-[#8a2020]"
        >
          {error}
        </div>
      )}

      {result && meta && (
        <section
          data-testid={`shield-result-${result.verdict}`}
          className="mt-8 space-y-5"
        >
          {/* Verdict banner */}
          <div
            className="rounded-2xl border-2 p-6"
            style={{ borderColor: meta.border, backgroundColor: meta.bg }}
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <meta.icon className="h-8 w-8" style={{ color: meta.color }} strokeWidth={2} />
                <div>
                  <p
                    className="text-sm font-semibold uppercase tracking-widest"
                    style={{ color: meta.color }}
                  >
                    {t(meta.labelKey)}
                  </p>
                  <p
                    className="font-serif text-3xl font-semibold"
                    style={{ color: meta.color }}
                  >
                    {result.risk_score}/100
                  </p>
                </div>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-4 py-2 text-base font-semibold ${meta.tone}`}
              >
                {result.verdict.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Flags */}
          {result.flags.length > 0 && (
            <div className="rounded-2xl border-2 border-[#E8DFC7] bg-white p-6">
              <h3 className="mb-4 font-serif text-2xl font-semibold text-[#101A42]">
                {t("shield_flags_title")}
              </h3>
              <ul className="space-y-4" data-testid="shield-flags-list">
                {result.flags.map((f, i) => (
                  <li
                    key={f.id}
                    data-testid={`shield-flag-${f.id}`}
                    className="rounded-xl border-l-4 border-[#B91C1C] bg-[#FEF2F2] p-4"
                  >
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#B91C1C]">
                      « {f.match} »
                    </p>
                    <p className="mt-2 text-base leading-relaxed text-[#101A42]">
                      {lang === "fr" ? f.fr : f.en}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 24h reflex */}
          {(result.verdict === "danger" || result.verdict === "caution") && (
            <div
              data-testid="shield-24h"
              className="rounded-2xl bg-[#101A42] p-6 text-white"
            >
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#F8EFD9]">
                <Clock className="h-4 w-4" />
                {t("shield_reflex_title")}
              </div>
              <p className="text-lg leading-relaxed">{t("shield_reflex_text")}</p>
            </div>
          )}

          {/* Actions */}
          {(result.verdict === "danger" || result.verdict === "caution") && (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border-2 border-[#B91C1C] bg-white p-4">
                <Ban className="mb-2 h-6 w-6 text-[#B91C1C]" />
                <p className="text-base font-semibold text-[#101A42]">
                  {t("shield_action_stop")}
                </p>
              </div>
              <a
                href="https://www.amf-france.org/fr/espace-epargnants/proteger-son-epargne/mise-en-garde"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shield-amf-link"
                className="rounded-xl border-2 border-[#101A42] bg-white p-4 hover:bg-[#101A42] hover:text-white"
              >
                <Phone className="mb-2 h-6 w-6" />
                <p className="text-base font-semibold">{t("shield_action_verify")}</p>
              </a>
              <div className="rounded-xl border-2 border-[#A762B1] bg-white p-4">
                <Users className="mb-2 h-6 w-6 text-[#A762B1]" />
                <p className="text-base font-semibold text-[#101A42]">
                  {t("shield_action_share")}
                </p>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
